import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { changeUsersFilters } from "../redux/team-members.slice.ts"
import type { TeamMembersFilters } from "../redux/team-members.types.ts"

const LABELS_PARAM = "labels"
const LABELS_PATTERN = /^\d+(,\d+)*$/

// A single id must reach redux as a number or the role select cannot match
// its option; a comma list has no numeric form and is passed through as-is.
const parseLabels = (raw: string) => (raw.includes(",") ? raw : Number(raw))

export function useLabelsUrlFilter(): { ready: boolean } {
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const labels = useAppSelector((state) => state.users.filters.labels)
  const [ready, setReady] = useState(false)
  const searchParamsRef = useRef(searchParams)

  searchParamsRef.current = searchParams

  useEffect(() => {
    const raw = searchParamsRef.current.get(LABELS_PARAM)

    if (raw && LABELS_PATTERN.test(raw)) {
      // page must reset with the filter: it is persisted, and an out-of-range
      // page makes DRF answer 404 instead of the filtered list.
      dispatch(
        changeUsersFilters({
          labels: parseLabels(raw) as unknown as TeamMembersFilters["labels"],
          page: 1
        })
      )
    }

    setReady(true)
  }, [dispatch])

  useEffect(() => {
    if (!ready) return

    const params = searchParamsRef.current
    const current = params.get(LABELS_PARAM)
    const next = labels === null || labels === undefined ? null : String(labels)

    if (current === next) return

    const updated = new URLSearchParams(params)
    if (next === null) updated.delete(LABELS_PARAM)
    else updated.set(LABELS_PARAM, next)

    setSearchParams(updated, { replace: true })
  }, [ready, labels, setSearchParams])

  return { ready }
}
